import { useState, useCallback } from 'react';
import type { DocumentData } from '../../hooks/useDocuments';
import { documentService } from '../../services/documentService';
import type { ViewMode } from '../../services/documentService';

interface UseSelectionModeOptions {
    feedData: DocumentData[];
    /** Total matching docs across all pages (from server) */
    total: number;
    /** Current filter params needed to fetch all matching IDs */
    filterParams: {
        search: string | null;
        view: ViewMode;
        tag: string | null;
        deletedOnly: boolean;
        /**
         * classifier-v3 (#1066): native (domain, role) lens axes scope cross-page
         * select-all to the active lens. The legacy category/subcategory filters
         * were removed with the classifier-v1 decommission (#2009).
         */
        domain?: string | null;
        role?: string | null;
    };
}

export interface UseSelectionModeReturn {
    selectionMode: boolean;
    selectedDocIds: Set<string>;
    handleEnterSelectionMode: () => void;
    handleExitSelectionMode: () => void;
    handleSelectionChange: (docId: string, selected: boolean) => void;
    handleSelectAll: () => void;
    handleDeselectAll: () => void;
    /** Whether all matching docs across all pages are selected */
    allMatchingSelected: boolean;
    /** Whether the "select all matching" banner should be shown */
    showSelectAllBanner: boolean;
    /** Handler to select all matching documents across all pages */
    handleSelectAllMatching: () => void;
    /** Whether we're currently loading all matching IDs */
    loadingAllIds: boolean;
    setSelectionMode: (mode: boolean) => void;
    setSelectedDocIds: (ids: Set<string>) => void;
}

export function useSelectionMode({ feedData, total, filterParams }: UseSelectionModeOptions): UseSelectionModeReturn {
    const [selectionMode, setSelectionMode] = useState<boolean>(false);
    const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
    const [allMatchingSelected, setAllMatchingSelected] = useState<boolean>(false);
    const [loadingAllIds, setLoadingAllIds] = useState<boolean>(false);

    const selectableOnPage = feedData.filter((doc) => doc.status === 'completed' || doc.status === 'error');
    const allOnPageSelected = selectableOnPage.length > 0 && selectableOnPage.every((doc) => selectedDocIds.has(doc.doc_id));
    const hasMorePages = total > selectableOnPage.length;

    // Show the "Select all X matching" banner when:
    // 1. All items on the current page are selected
    // 2. There are more matching docs beyond this page
    // 3. We haven't already selected all matching
    const showSelectAllBanner = allOnPageSelected && hasMorePages && !allMatchingSelected;

    const handleEnterSelectionMode = useCallback(() => {
        setSelectionMode(true);
        setSelectedDocIds(new Set());
        setAllMatchingSelected(false);
    }, []);

    const handleExitSelectionMode = useCallback(() => {
        setSelectionMode(false);
        setSelectedDocIds(new Set());
        setAllMatchingSelected(false);
    }, []);

    const handleSelectionChange = useCallback((docId: string, selected: boolean) => {
        setAllMatchingSelected(false);
        setSelectedDocIds((prev) => {
            const newSet = new Set(prev);
            if (selected) {
                newSet.add(docId);
            } else {
                newSet.delete(docId);
            }
            return newSet;
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        const selectableDocIds = feedData
            .filter((doc) => doc.status === 'completed' || doc.status === 'error')
            .map((doc) => doc.doc_id);
        setSelectedDocIds(new Set(selectableDocIds));
    }, [feedData]);

    const handleDeselectAll = useCallback(() => {
        setSelectedDocIds(new Set());
        setAllMatchingSelected(false);
    }, []);

    const handleSelectAllMatching = useCallback(async () => {
        setLoadingAllIds(true);
        try {
            const params: Record<string, string | boolean> = {};
            if (filterParams.search) params.search = filterParams.search;
            if (filterParams.tag) params.tag = filterParams.tag;
            // classifier-v3 (#1066): forward the active lens so select-all stays lens-scoped.
            if (filterParams.domain) params.domain = filterParams.domain;
            if (filterParams.role) params.role = filterParams.role;
            if (filterParams.deletedOnly) params.deleted_only = true;
            params.view = filterParams.view;

            const allIds = await documentService.getAllMatchingIds(params);
            setSelectedDocIds(new Set(allIds));
            setAllMatchingSelected(true);
        } catch (err) {
            console.error('Failed to fetch all matching document IDs:', err);
        } finally {
            setLoadingAllIds(false);
        }
    }, [filterParams]);

    return {
        selectionMode,
        selectedDocIds,
        handleEnterSelectionMode,
        handleExitSelectionMode,
        handleSelectionChange,
        handleSelectAll,
        handleDeselectAll,
        allMatchingSelected,
        showSelectAllBanner,
        handleSelectAllMatching,
        loadingAllIds,
        setSelectionMode,
        setSelectedDocIds,
    };
}
